/**
 * One-time "this is an alpha" banner.
 *
 * The previous implementation also ran a `TreeWalker` over the whole document
 * rewriting the word "beta" to "alpha" on a timer, which is what blanked the
 * page in three separate bug fixes. Those strings are now correct in
 * `public/legacy/core.js`, so nothing rewrites live text any more.
 */

import { isNative } from '../native/platform.js';

const DISMISSED_KEY = 'alpha_banner_dismissed';

function isDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // Private mode / storage disabled — show the banner, don't crash.
    return false;
  }
}

function remember() {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    /* nothing to do */
  }
}

/**
 * Strip the "ALPHA" pills baked into index.html.
 *
 * They are honest on the web, where the audience is people who agreed to test
 * something unfinished. In a store build they are a liability: App Store Review
 * Guideline 2.2 rejects "apps that are beta, demo, trial, or test versions",
 * and an app that greets the reviewer with a badge reading ALPHA and a banner
 * promising rough edges is arguing their case for them. Play's pre-launch
 * policy takes the same line.
 */
function removeReleaseBadges() {
  document.querySelectorAll('.release-badge').forEach((el) => el.remove());
}

export function installAlphaBanner() {
  // Native means a store build. Nothing about being unfinished ships there.
  if (isNative) {
    if (document.body) removeReleaseBadges();
    else document.addEventListener('DOMContentLoaded', removeReleaseBadges, { once: true });
    return;
  }

  if (isDismissed()) return;

  const banner = document.createElement('div');
  banner.id = 'alpha-banner';
  banner.setAttribute('role', 'status');
  banner.innerHTML = `
    <span aria-hidden="true">🔥</span>
    <span><strong>IronPact Alpha</strong> — You're one of our first testers. Expect rough edges and share your feedback!</span>
    <button type="button" id="alpha-banner-dismiss">Got it</button>
  `;

  const mount = () => {
    document.body.prepend(banner);
    // The banner is fixed-position, so the shell needs to shed its height.
    document.documentElement.style.setProperty(
      '--alpha-banner-height',
      `${banner.offsetHeight}px`,
    );
    document.body.classList.add('has-alpha-banner');

    banner.querySelector('#alpha-banner-dismiss').addEventListener('click', () => {
      banner.remove();
      document.body.classList.remove('has-alpha-banner');
      document.documentElement.style.removeProperty('--alpha-banner-height');
      remember();
    });
  };

  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });
}
